import React from 'react';
import { renderToString } from 'react-dom/server';
import PropTypes from 'prop-types';

import CardItem from './CardItem';
import { formatAddress, formatPhone } from '../../lib/LocationDetailsUtils';

/**
 * Render the "coming soon" copy if a location is upcoming (not yet available).
 *
 * @param {Boolean} isUpcoming - Whether or not a location is upcoming.
 * @param {String} label - Directions link label.
 * @return {String} - Markup for upcoming copy.
 */
const upcoming = (isUpcoming, label, type) => {
  if (type === 'supercharger') {
    return '';
  }

  return isUpcoming ? label : null;
};

/**
 * Render primary link if it exists.
 *
 * @param {String} link - Link target.
 * @param {String} label - Link label.
 * @return {String} - Markup for link.
 */
const primaryLink = (link, label) =>
  link && label
    ? renderToString(
        <a
          className="tsla-link tsla-link--primary"
          rel="noopener noreferrer"
          target="_blank"
          href={link}
        >
          {label}
        </a>
      )
    : null;

/**
 * Render phone numbers if any exist.
 *
 * @param {Array<Object>} phones - Array of phone number objects.
 * @return {String} - Markup for phone numbers.
 */
const phone = phones => {
  if (phones && phones.length > 0) {
    const phonesMarkup = (
      <div>
        {phones
          .filter(item => item.number)
          .map(item => (
            <p className="card-phone" key={`location-${item.number}`}>
              <span className="card-phone_label">{item.label}</span>
              <span className="card-phone_number">
                <a href={`tel:${formatPhone(item.number)}`}>{item.number}</a>
                <span>{item.number}</span>
              </span>
            </p>
          ))}
      </div>
    );

    return renderToString(phonesMarkup);
  }

  return null;
};

const cardItems = (location, content) => [
  {
    markup: upcoming(location.upcoming, content.body.newLocation.label, location.types[0]),
    label: 'upcoming',
  },
  {
    markup: !location.upcoming ? formatAddress(location) : null,
    label: 'address',
  },
  {
    markup: location.notes,
    label: 'notes',
  },
  {
    markup: primaryLink(location.directions, content.body.directions),
    label: 'directions',
  },
  {
    markup: primaryLink(location.website, `${location.website} →`),
    label: 'website',
  },
  {
    markup: phone(location.phone),
    label: 'phones',
  },
  {
    markup: location.chargers,
    label: 'chargers',
  },
  {
    markup: location.amenities,
    label: 'amenities',
  },
  {
    markup: location.hours,
    label: 'hours',
  },
];

class CardBody extends React.Component {
  constructor(props) {
    super(props);

    // Set body ref.
    this.setRef = ref => {
      this.bodyRef = ref;
      if (this.props.bodyRef) this.props.bodyRef(ref);
    };
  }

  componentWillReceiveProps(newProps) {
    // Scroll card back up to the top when the location is changed.
    if (newProps.location !== this.props.location) {
      this.bodyRef.scrollTop = 0;
    }
  }

  render() {
    const { location, content } = this.props;

    return (
      <div className="card-body" ref={this.setRef}>
        <h2 className="card-item card-item--name">{location.name}</h2>
        {cardItems(location, content).map(item => (
          <CardItem key={`card-${item.label}`} markup={item.markup} label={item.label} />
        ))}
      </div>
    );
  }
}

CardBody.defaultProps = {
  bodyRef: () => {},
  content: {},
  location: {},
};

CardBody.propTypes = {
  location: PropTypes.shape({ id: PropTypes.string }),
  content: PropTypes.objectOf(PropTypes.object),
  bodyRef: PropTypes.func,
};

export default CardBody;
